import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { useCallback, useEffect, useState, type FC } from 'react';
import QRCode from 'qrcode';
import { Icon } from 'src/common/components/icon';
import { writeTextInClipboard } from 'src/utils/clipboard';
import { SearchParams } from 'src/utils';
import { useCurrentUser } from 'src/hooks/user';
import { useGetSharePageUrl } from './helper';
import { useOpenSharePoster } from './share-poster';
import { bizTracker } from 'src/utils/biz-tracker';
import { usePayInfo } from 'src/hooks/page/use-pay-info';

interface Props {
  pageId: string;
}
export const ShareDialog: FC<Props> = (props) => {
  const { t } = useAppTranslation();
  const [qrCode, setQrCode] = useState<string>('');
  const { amount = 0, payInfo } = usePayInfo(props.pageId);
  const currentUser = useCurrentUser();
  const getSharePageUrl = useGetSharePageUrl(props.pageId);
  const { url, textAndUrl } = getSharePageUrl({ inviteCode: currentUser.inviteCode });

  const copyEmbedLink = useCallback(async () => {
    bizTracker.event('click_share_btn', {
      type: 'embedded',
    });
    const urlObj = new URL(url);
    urlObj.searchParams.set(SearchParams.embed, 'true');
    await writeTextInClipboard(urlObj.href, {
      message: t('common.link_copied'),
    });
  }, [url, t]);
  const onCopy = useCallback(() => {
    bizTracker.event('click_share_btn', {
      type: 'link',
    });
    void writeTextInClipboard(textAndUrl, {
      message: t('common.link_copied'),
    });
  }, [textAndUrl, t]);

  const openSharePoster = useOpenSharePoster();

  useEffect(() => {
    void (async () => {
      // 二维码url
      const _qr = await QRCode.toDataURL(`${url}`, {
        errorCorrectionLevel: 'medium',
      });
      setQrCode(_qr);
    })();
  }, [url]);
  return (
    <div className="next-modal p-4 space-y-2 w-64">
      <div className="text-center w-full text-t2-medium py-1">
        {__BUILD_IN__ || !(payInfo?.sharePageDistributionRatio && payInfo.accessFee.toFixed(2)) ? (
          t('common.share')
        ) : (
          <div className="space-y-1.5 text-t3">
            <div>{t('components.share.share_dialog.earn_commission_by_sharing')}</div>
            <span>
              {t('components.share.share_dialog.commission_description', {
                ratio: payInfo.sharePageDistributionRatio * 100,
              })}
            </span>
            <span className="text-red">
              {amount}
              {t('common.yuan')}
            </span>
          </div>
        )}
      </div>
      <div className="flex justify-center">
        <img className="w-[120px] h-[120px]" src={qrCode} />
      </div>
      <div className="flex justify-center">
        <div className="flex items-center space-x-1">
          <div className="bg-grey8 h-px w-8" />
          <div className="text-grey3 text-t4-medium">{t('common.other_share_methods')}</div>
          <div className="bg-grey8 h-px w-8" />
        </div>
      </div>
      <div className="flex items-center justify-around text-grey3">
        <div className="flex flex-col items-center  p-1" onClick={onCopy}>
          <div className="flex justify-center items-center w-8 h-8 rounded-full bg-grey9 animate-hover">
            <Icon name={'IcLinkSmall'} size="middle" className="text-grey1" />
          </div>
          <div className="mt-1 text-t4">{t('common.link_share')}</div>
        </div>
        <div className="flex flex-col items-center p-1" onClick={copyEmbedLink}>
          <div className="flex justify-center items-center w-8 h-8 rounded-full bg-grey9 animate-hover">
            <Icon name={'IcEmbeddedLinkSmall'} size="middle" className="text-grey1" />
          </div>
          <div className="mt-1 text-t4">{t('components.share.share_dialog.embed_sharing')}</div>
        </div>
        {!__BUILD_IN__ && (
          <div
            className="flex flex-col items-center p-1"
            onClick={() => {
              openSharePoster({
                url,
                pageId: props.pageId,
              });
            }}
          >
            <div className="flex  justify-center items-center w-8 h-8 rounded-full bg-grey9  animate-hover">
              <Icon name={'IcImageSmall'} size="middle" />
            </div>
            <div className="mt-1 text-t4">{t('common.generate_poster')}</div>
          </div>
        )}
      </div>
    </div>
  );
};
